/*
  Anonymous aggregate counters ("event X on page Y, +1"). Counts every visitor, consent or not. We set no
  cookie, use no localStorage / sessionStorage and keep no id; our table stores no IP. Each payload carries
  only event name, page type, product handle, mobile/desktop, one small per-event detail and, on attribution
  events only (page_view, page_dwell, view_item, add_to_cart, begin_checkout), utm_content and the traffic
  source of the current page view. The request itself (like any web request) reaches Supabase with IP, user
  agent, language and our origin; it is sent with credentials omitted and no referrer.
  Receiver: Supabase function tg_count_beacon (services/api/migrations/2026-10-09_anonymous_event_counters*.sql).
  Always on: window.tgAggregateConfig is rendered by layout/theme.liquid. Instant kill switch without a deploy:
  revoke execute on tg_count_beacon from anon.
  Legal notes: docs/business/legal/2026-10-09-anonymous-event-counters.md
*/
(function () {
  'use strict';

  var cfg = window.tgAggregateConfig;
  if (!cfg || !cfg.url || !cfg.key) return;

  var h = window.location.hostname;
  if (h === 'localhost' || h === '127.0.0.1' || h.indexOf('192.168.') === 0) return;
  if (window.Shopify && window.Shopify.designMode) return;
  if (window.tgInternal || navigator.webdriver) return;
  // Meta ad-review crawlers render a 2000x2000 viewport and run JS; they cannot be removed afterwards.
  if (window.innerWidth >= 1990 && window.innerHeight >= 1990) return;

  var PAGE_TYPES = { home: 1, collection: 1, product: 1, cart: 1 };
  function same(key) { return function (p) { return p[key]; }; }
  function none() { return ''; }

  // Event name -> the one detail value kept for it. Names missing here are never sent.
  var DETAIL = {
    page_view: none, page_dwell: same('dwell'), view_item: none, add_to_cart: none, begin_checkout: none, view_bundle_upsell: none,
    lightbox_media_view: none, pdp_image_zoom: none, accordion_open: none, open_duet_modal: none,
    restock_notify_submit: none, newsletter_signup: none,
    view_section: same('section_id'), view_cta: same('cta_id'), cta_click: same('cta_id'),
    allegro_click: same('location'), tooltip_open: same('location'), pack_preview_open: same('location'),
    gallery_slide_change: same('method'), gallery_depth: same('max_media_position'),
    card_slider_change: same('slider_position'), card_sample_zoom: same('media_position'),
    reviews_slide_change: same('review_position'), buybox_select: same('bundle'),
    buybox_chooser_open: same('bundle'), pdp_exit_without_cta: same('last_section_seen'),
    slider_change: function (p) { return p.slider + ':' + p.slider_position; }
  };

  function clean(v, max, extra) {
    if (v === undefined || v === null) return '';
    return String(v).toLowerCase().replace(extra ? /[^a-z0-9_:.-]/g : /[^a-z0-9_-]/g, '-').slice(0, max);
  }

  function pageTypeOf(path) {
    if (path === '/' || path === '') return 'home';
    if (path.indexOf('/products/') === 0) return 'product';
    if (path.indexOf('/collections/') === 0) return 'collection';
    if (path.indexOf('/cart') === 0) return 'cart';
    return 'other';
  }

  // Markets live in subfolders (/en/, /de/, /pl/): drop the locale prefix before matching.
  var path = window.location.pathname.replace(/^\/[a-z]{2}(?:-[a-z]{2})?(?=\/)/, '');
  var match = /^\/products\/([a-z0-9-]+)/.exec(path);
  var pathProduct = match ? match[1] : '';
  var creative = '';
  try { creative = clean(new URLSearchParams(window.location.search).get('utm_content'), 60, false); } catch (e) {}
  var device = window.matchMedia && window.matchMedia('(max-width: 749px)').matches ? 'mobile' : 'desktop';

  // Ad creative (utm_content) and traffic source are kept only on these events: on click events a low-count
  // row could be joined to the few orders we hold per creative / source and day.
  var ATTRIBUTION_EVENTS = { page_view: 1, page_dwell: 1, view_item: 1, add_to_cart: 1, begin_checkout: 1 };

  // utm values are free text in the URL (anyone can craft a link), so only known names are kept; the rest
  // becomes 'other'. To track a new source or medium, add it here.
  var SOURCES = { facebook: 1, instagram: 1, google: 1, 'chatgpt.com': 1, chatgpt: 1, klaviyo: 1, allegro: 1,
    tiktok: 1, bing: 1, youtube: 1, newsletter: 1, perplexity: 1, pinterest: 1, reddit: 1 };
  var MEDIUMS = { paid_social: 1, cpc: 1, email: 1, social: 1, organic: 1, referral: 1, paid: 1, cpm: 1, display: 1 };

  // Source of THIS page view only (nothing is stored between pages): utm_source[:utm_medium] when the URL
  // has them (ads, ChatGPT), else a coarse bucket of document.referrer's host (never its path), else
  // 'direct'. Internal navigation gives '' (the landing page already carried the source).
  function sourceOf() {
    try {
      var q = new URLSearchParams(window.location.search);
      var us = q.get('utm_source');
      if (us) {
        var s1 = clean(us, 24, true);
        var um = clean(q.get('utm_medium') || '', 14, true);
        return (SOURCES[s1] ? s1 : 'other') + (MEDIUMS[um] ? ':' + um : '');
      }
    } catch (e) {}
    var ref = document.referrer;
    if (!ref) return 'direct';
    var u;
    try { u = new URL(ref); } catch (e) { return 'other'; }
    var host = u.hostname.replace(/^www\./, '').toLowerCase();
    if (u.protocol === 'android-app:') {
      if (/^com\.facebook\./.test(host)) return 'facebook';
      if (/^com\.instagram\./.test(host)) return 'instagram';
      if (host === 'com.google.android.googlequicksearchbox') return 'google';
      return 'other';
    }
    if (u.protocol !== 'https:' && u.protocol !== 'http:') return 'other';
    if (host === window.location.hostname.replace(/^www\./, '') || /(^|\.)togethergames\.(eu|pl)$/.test(host) ||
        /(^|\.)(myshopify|shopify)\.com$/.test(host)) return '';
    if (/(^|\.)google\.[a-z.]+$/.test(host)) return 'google';
    if (/(^|\.)(facebook|fb)\.com$/.test(host)) return 'facebook';
    if (/(^|\.)instagram\.com$/.test(host)) return 'instagram';
    if (/(^|\.)(chatgpt|openai)\.com$/.test(host)) return 'chatgpt';
    if (/(^|\.)perplexity\.ai$/.test(host)) return 'perplexity';
    if (/(^|\.)bing\.com$/.test(host)) return 'bing';
    if (/(^|\.)duckduckgo\.com$/.test(host)) return 'duckduckgo';
    return 'other';
  }
  var source = sourceOf();

  var queue = [];
  var timer = null;
  var leaving = false;

  // fetch with keepalive is built for page exit (the browser finishes it after the tab is gone) and, unlike
  // sendBeacon, can omit credentials: a beacon would store and resend Supabase's Cloudflare cookie.
  // text/plain keeps it a CORS "simple request" (no preflight); the key travels in the query string.
  // No sendBeacon fallback: it would store and resend that cookie. Browsers without fetch keepalive are not counted.
  function send(events) {
    var url = cfg.url + '?apikey=' + encodeURIComponent(cfg.key);
    var body = JSON.stringify(events);
    try {
      if (window.fetch && 'keepalive' in Request.prototype) {
        fetch(url, {
          method: 'POST',
          keepalive: true,
          credentials: 'omit',
          referrerPolicy: 'no-referrer',
          headers: { 'Content-Type': 'text/plain;charset=UTF-8' },
          body: body
        }).catch(function () {});
        return;
      }
    } catch (e) {}
  }

  function flush() {
    if (timer) { clearTimeout(timer); timer = null; }
    while (queue.length) send(queue.splice(0, 25));
  }

  function add(name, params) {
    try { addUnsafe(name, params); } catch (e) {}
  }

  function addUnsafe(name, params) {
    var detail = DETAIL[name];
    if (!detail) return;
    params = params || {};
    var pt = params.page_type || pageTypeOf(path);
    queue.push({
      e: name,
      pt: PAGE_TYPES[pt] ? pt : 'other',
      p: clean(params.product || pathProduct, 80, false).replace(/_/g, '-'),
      c: ATTRIBUTION_EVENTS[name] ? creative : '',
      s: ATTRIBUTION_EVENTS[name] ? source : '',
      d: device,
      x: clean(detail(params), 60, true)
    });
    // Events queued while the page is leaving (gallery_depth, pdp_exit_without_cta fire in their own
    // pagehide handlers, after ours) are sent at once, there is no later chance.
    if (leaving || document.visibilityState === 'hidden' || queue.length >= 20) flush();
    else if (!timer) timer = setTimeout(flush, 10000);
  }

  window.tgAggregate = { add: add };

  // Time on page (visible time until the page is first hidden or left), in coarse buckets. Memory only.
  var shownAt = document.visibilityState === 'visible' ? Date.now() : null;
  var visibleMs = 0;
  var everVisible = shownAt !== null;
  var dwellSent = false;
  function dwell() {
    if (dwellSent || !everVisible) return;
    dwellSent = true;
    if (shownAt) visibleMs += Date.now() - shownAt;
    shownAt = null;
    var sec = visibleMs / 1000;
    add('page_dwell', { dwell: sec < 5 ? 'lt5' : sec < 15 ? '5_15' : sec < 30 ? '15_30' : sec < 60 ? '30_60' : sec < 120 ? '60_120' : sec < 300 ? '120_300' : '300p' });
  }

  document.addEventListener('visibilitychange', function () {
    if (document.visibilityState === 'hidden') { dwell(); flush(); }
    else if (!dwellSent) { shownAt = Date.now(); everVisible = true; }
  });
  window.addEventListener('pagehide', function () { leaving = true; dwell(); flush(); });
  window.addEventListener('pageshow', function () { leaving = false; });

  // A prerendered page nobody opens must not count as a view: wait until it is activated.
  function firstView() { add('page_view', { page_type: pageTypeOf(path) }); }
  if (document.prerendering) document.addEventListener('prerenderingchange', firstView, { once: true });
  else firstView();
})();
